import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { getInvoiceByIdOptions, getInvoiceItemsOptions, getAllInvoicesQueryKey } from "@/lib/client/@tanstack/react-query.gen";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import {
	ArrowLeft,
	Receipt,
	User,
	Calendar,
	Euro,
	CreditCard,
	AlertCircle,
	CheckCircle,
	XCircle,
	ExternalLink,
	FileText,
	DollarSign,
	Ban,
	Pencil,
} from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

export const Route = createFileRoute("/admin/invoices/$id")({
	component: InvoiceDetailPage,
});

function InvoiceDetailPage() {
	const { id } = Route.useParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const cancelInvoice = useMutation({
		mutationFn: async () => {
			const response = await fetch(
				`${import.meta.env.VITE_API_URL}/api/admin/invoices/${id}/cancel`,
				{
					method: "POST",
					headers: {
						Authorization: `Bearer ${localStorage.getItem("token")}`,
					},
				},
			);
			if (!response.ok) {
				const message = await response.text().catch(() => "");
				throw new Error(message || "Erreur lors de l'annulation");
			}
			return response.json();
		},
		onSuccess: () => {
			toast.success("Facture annulée avec succès");
			queryClient.invalidateQueries({
				queryKey: getInvoiceByIdOptions({ path: { id } }).queryKey,
			});
			queryClient.invalidateQueries({ queryKey: getAllInvoicesQueryKey() });
		},
		onError: (error: Error) => {
			toast.error(error.message || "Erreur lors de l'annulation de la facture");
		},
	});

	const { data: invoice, isLoading } = useQuery(
		getInvoiceByIdOptions({
			path: { id },
		}),
	) as { data: any; isLoading: boolean };

	const { data: invoiceItems, isLoading: itemsLoading } = useQuery({
		...getInvoiceItemsOptions({
			path: { id },
		}),
		enabled: !!invoice,
	});

	const statusConfig: Record<
		string,
		{
			label: string;
			variant: "default" | "secondary" | "destructive" | "outline";
			icon: any;
			color: string;
		}
	> = {
		PENDING: {
			label: "En attente",
			variant: "secondary",
			icon: AlertCircle,
			color: "text-yellow-600",
		},
		PAID: {
			label: "Payée",
			variant: "default",
			icon: CheckCircle,
			color: "text-green-600",
		},
		FAILED: {
			label: "Échec",
			variant: "destructive",
			icon: XCircle,
			color: "text-red-600",
		},
		CANCELLED: {
			label: "Annulée",
			variant: "outline",
			icon: XCircle,
			color: "text-gray-600",
		},
	};

	const paymentStatusConfig: Record<
		string,
		{
			label: string;
			variant: "default" | "secondary" | "destructive" | "outline";
			icon: any;
		}
	> = {
		PENDING: { label: "En attente", variant: "secondary", icon: AlertCircle },
		PROCESSING: { label: "En cours", variant: "secondary", icon: AlertCircle },
		SUCCEEDED: { label: "Réussi", variant: "default", icon: CheckCircle },
		FAILED: { label: "Échec", variant: "destructive", icon: XCircle },
		REFUNDED: { label: "Remboursé", variant: "outline", icon: CheckCircle },
	};

	if (isLoading) {
		return (
			<div className="flex items-center justify-center h-96">
				<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
			</div>
		);
	}

	if (!invoice) {
		return (
			<div className="p-6">
				<div className="text-center">
					<h2 className="text-2xl font-bold text-gray-900 mb-4">
						Facture non trouvée
					</h2>
					<Link to="/admin/invoices">
						<Button variant="outline">
							<ArrowLeft className="mr-2 h-4 w-4" />
							Retour à la liste
						</Button>
					</Link>
				</div>
			</div>
		);
	}

	const status = statusConfig[invoice.status] || statusConfig.PENDING;
	const StatusIcon = status.icon;

	return (
		<div className="space-y-6">
			<div className="flex items-center gap-4">
				<Button
					variant="ghost"
					size="sm"
					onClick={() => navigate({ to: "/admin/invoices" })}
				>
					<ArrowLeft className="h-4 w-4 mr-2" />
					Retour
				</Button>
			</div>

			<div className="flex justify-between items-start">
				<div>
					<h1 className="text-3xl font-bold">
						Facture #{invoice.invoiceNumber}
					</h1>
					<p className="text-gray-600 mt-2">
						Du{" "}
						{format(new Date(invoice.billingPeriodStart), "dd/MM/yyyy", {
							locale: fr,
						})}{" "}
						au{" "}
						{format(new Date(invoice.billingPeriodEnd), "dd/MM/yyyy", {
							locale: fr,
						})}
					</p>
				</div>
				<div className="flex items-center gap-2">
					<Badge variant={status.variant} className="text-base py-1 px-3">
						<StatusIcon className={`h-4 w-4 mr-1 ${status.color}`} />
						{status.label}
					</Badge>
					{invoice.status !== "PAID" && invoice.status !== "CANCELLED" && (
						<Button
							variant="default"
							size="sm"
							onClick={() =>
								navigate({
									to: "/admin/payments",
									search: { invoiceId: invoice.id, parentId: invoice.parent?.id },
								})
							}
						>
							<DollarSign className="h-4 w-4 mr-2" />
							Paiement manuel
						</Button>
					)}
					<Button
						variant="outline"
						size="sm"
						onClick={() => window.open(`${import.meta.env.VITE_API_URL}/api/public/invoices/${invoice.id}/pdf`, '_blank')}
					>
						<FileText className="h-4 w-4 mr-2" />
						Voir la facture
					</Button>
					{invoice.status !== "PAID" && invoice.status !== "CANCELLED" && (
						<Button
							variant="outline"
							size="sm"
							onClick={() =>
								navigate({
									to: "/admin/invoices/correct/$id",
									params: { id: invoice.id },
								})
							}
						>
							<Pencil className="h-4 w-4 mr-2" />
							Corriger
						</Button>
					)}
					{invoice.status !== "PAID" && invoice.status !== "CANCELLED" && (
						<Button
							variant="destructive"
							size="sm"
							onClick={() => {
								if (
									confirm(
										"Êtes-vous sûr de vouloir annuler cette facture ? Cette action est irréversible.",
									)
								) {
									cancelInvoice.mutate();
								}
							}}
							disabled={cancelInvoice.isPending}
						>
							<Ban className="h-4 w-4 mr-2" />
							Annuler la facture
						</Button>
					)}
					{invoice.uniquePaymentLink && (
						<a
							href={`/facture/${invoice.uniquePaymentLink}`}
							target="_blank"
							rel="noopener noreferrer"
						>
							<Button variant="outline" size="sm">
								<ExternalLink className="h-4 w-4 mr-2" />
								Page de paiement
							</Button>
						</a>
					)}
				</div>
			</div>

			<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<User className="h-5 w-5" />
							Informations du parent
						</CardTitle>
					</CardHeader>
					<CardContent className="space-y-4">
						<div>
							<p className="text-sm text-gray-600">Parent</p>
							<p className="font-medium">
								{invoice.parent.firstName} {invoice.parent.lastName}
							</p>
						</div>
						<div>
							<p className="text-sm text-gray-600">Email</p>
							<p className="font-medium">{invoice.parent.email}</p>
						</div>
						<div>
							<p className="text-sm text-gray-600">Téléphone</p>
							<p className="font-medium">{invoice.parent.phone || "-"}</p>
						</div>
						<div className="pt-2">
							<Link to="/admin/parents/$id" params={{ id: invoice.parent.id }}>
								<Button size="sm" variant="outline">
									<User className="h-3 w-3 mr-2" />
									Voir le parent
								</Button>
							</Link>
						</div>
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<Receipt className="h-5 w-5" />
							Détails de la facture
						</CardTitle>
					</CardHeader>
					<CardContent className="space-y-4">
						<div>
							<p className="text-sm text-gray-600">Période</p>
							<div className="flex items-center gap-2">
								<Calendar className="h-4 w-4 text-gray-400" />
								<p className="font-medium">
									Du{" "}
									{format(new Date(invoice.billingPeriodStart), "dd/MM/yyyy", {
										locale: fr,
									})}{" "}
									au{" "}
									{format(new Date(invoice.billingPeriodEnd), "dd/MM/yyyy", {
										locale: fr,
									})}
								</p>
							</div>
						</div>
						<div>
							<p className="text-sm text-gray-600">Montant total</p>
							<div className="flex items-center gap-2">
								<Euro className="h-4 w-4 text-green-600" />
								<p className="text-2xl font-bold">
									{invoice.totalAmount?.toFixed(2)} €
								</p>
							</div>
						</div>
						<div>
							<p className="text-sm text-gray-600">Échéance</p>
							<div className="flex items-center gap-2">
								<Calendar className="h-4 w-4 text-gray-400" />
								<p className="font-medium">
									{format(new Date(invoice.dueDate), "dd/MM/yyyy", {
										locale: fr,
									})}
								</p>
							</div>
						</div>
						<div>
							<p className="text-sm text-gray-600">Date de création</p>
							<p className="font-medium">
								{format(new Date(invoice.createdAt), "dd/MM/yyyy à HH:mm", {
									locale: fr,
								})}
							</p>
						</div>
					</CardContent>
				</Card>
			</div>

			<Card>
				<CardHeader>
					<CardTitle>Détail des cours facturés</CardTitle>
					<CardDescription>
						Résumé des cours par élève avec nombre de présences
					</CardDescription>
				</CardHeader>
				<CardContent>
					{itemsLoading ? (
						<p className="text-sm text-gray-500">Chargement...</p>
					) : invoiceItems && invoiceItems.length > 0 ? (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Élève</TableHead>
									<TableHead>École</TableHead>
									<TableHead>Matière</TableHead>
									<TableHead>Niveau</TableHead>
									<TableHead>Créneau</TableHead>
									<TableHead className="text-right">Heures</TableHead>
									<TableHead className="text-right">Prix/heure</TableHead>
									<TableHead className="text-right">Total</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{invoiceItems.map((item: any, idx: number) => (
									<>
										<TableRow key={idx} className={item.hours === 0 ? "bg-gray-50" : ""}>
											<TableCell className="font-medium">{item.studentName}</TableCell>
											<TableCell className="text-sm">{item.studentSchool}</TableCell>
											<TableCell>{item.courseSubject}</TableCell>
											<TableCell className="text-sm">{item.courseLevel}</TableCell>
											<TableCell className="text-sm">{item.courseTimeSlot}</TableCell>
											<TableCell className="text-right">
												<span className={item.hours === 0 ? "text-gray-400" : ""}>
													{item.hours}h
												</span>
											</TableCell>
											<TableCell className="text-right">{item.unitPricePerHour?.toFixed(2)} €</TableCell>
											<TableCell className="text-right font-medium">
												{item.totalAmount?.toFixed(2)} €
											</TableCell>
										</TableRow>
										{item.attendanceDetails && item.attendanceDetails.length > 0 && (
											<TableRow key={`${idx}-details`}>
												<TableCell colSpan={8} className="bg-gray-50 text-xs text-gray-600 py-2">
													<div className="space-y-1">
														{item.attendanceDetails.map((attendance: any, attIdx: number) => (
															<div key={attIdx} className="flex items-center gap-2">
																<span className="font-medium">
																	{format(new Date(attendance.date), 'dd/MM/yyyy', { locale: fr })}:
																</span>
																<span className="text-gray-700">{attendance.status}</span>
																{attendance.hours > 0 && (
																	<span className="text-gray-700">{attendance.hours}h</span>
																)}
															</div>
														))}
													</div>
												</TableCell>
											</TableRow>
										)}
									</>
								))}
								<TableRow className="font-bold bg-gray-100">
									<TableCell colSpan={7} className="text-right">TOTAL</TableCell>
									<TableCell className="text-right">
										{invoice.totalAmount?.toFixed(2)} €
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					) : (
						<p className="text-sm text-gray-500">Aucun cours facturé</p>
					)}
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle>Historique des paiements</CardTitle>
					<CardDescription>
						Liste de tous les paiements tentés pour cette facture
					</CardDescription>
				</CardHeader>
				<CardContent>
					{invoice.payments && invoice.payments.length > 0 ? (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Date</TableHead>
									<TableHead>Montant</TableHead>
									<TableHead>Méthode</TableHead>
									<TableHead>Statut</TableHead>
									<TableHead>Erreur</TableHead>
									<TableHead>Actions</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{invoice.payments.map((payment: any) => {
									const paymentStatus =
										paymentStatusConfig[payment.status] ||
										paymentStatusConfig.PENDING;
									const PaymentIcon = paymentStatus.icon;

									return (
										<TableRow key={payment.id}>
											<TableCell className="text-sm">
												{format(
													new Date(payment.createdAt),
													"dd/MM/yyyy HH:mm",
													{ locale: fr },
												)}
											</TableCell>
											<TableCell className="font-medium">
												{payment.amount?.toFixed(2)} €
											</TableCell>
											<TableCell className="text-sm">
												{payment.paymentMethod === "CARD" && "Carte"}
												{payment.paymentMethod === "SEPA" && "SEPA"}
												{payment.paymentMethod === "BANK_TRANSFER" &&
													"Virement"}
											</TableCell>
											<TableCell>
												<Badge variant={paymentStatus.variant}>
													<PaymentIcon className="h-3 w-3 mr-1" />
													{paymentStatus.label}
												</Badge>
											</TableCell>
											<TableCell className="text-sm text-gray-600 max-w-xs truncate">
												{payment.failureMessage || "-"}
											</TableCell>
											<TableCell>
												<Link
													to="/admin/payments/$id"
													params={{ id: payment.id }}
												>
													<Button size="sm" variant="outline">
														Voir
													</Button>
												</Link>
											</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					) : (
						<div className="text-center py-12">
							<CreditCard className="mx-auto h-12 w-12 text-gray-400 mb-4" />
							<p className="text-gray-500 font-medium">
								Aucun paiement enregistré
							</p>
							<p className="text-gray-400 text-sm mt-1">
								Aucune tentative de paiement n'a été effectuée pour cette
								facture
							</p>
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
